import React, { useState, useEffect, useContext } from "react";
//import { renderCount } from '../../../../appHelpers/appLoggers';
import styled from "styled-components";
import { FlexboxGrid } from "rsuite";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../appHelpers/axiosInstance";
import ImageIcon from "@rsuite/icons/Image";
import { InputContext } from "../_context/InputContext";
import { sanitizeImageUrl } from "../_utils/util_general";
import DOMPurify from "dompurify";
import { GlassCard, Loader, Badge, SearchField } from "../../../ui";

export const CustomerListFC = (props) => {
  // renderCount(`AdminFC`);
  const { getAccessTokenSilently } = useAuth0();
  const [displayClients, setDisplayClients] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientLogos, setClientLogos] = useState();
  const [loadingClients, setLoadingClients] = useState(true);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const { adminCustomerToggle, setAdminCustomerToggle } =
    useContext(InputContext);

  const sanitizeCustomerLogo = (url) => {
    return <ImgSrcLogo src={DOMPurify.sanitize(url)} />;
  };

  const getClientList = async () => {
    setLoadingClients(true);
    setCardsVisible(false);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const clientRes = await axiosInstance({
        url: `/admin/customer/list`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setClients(clientRes?.data[0]);
      setClientLogos(
        clientRes?.data[0]?.map((client) => sanitizeImageUrl(client.logo_src)),
      );
    } catch (error) {
      console.error(error);
    }
    setLoadingClients(false);
    // Trigger staggered animation
    setCardsVisible(true);
  };

  const handleSelect = (e) => {
    setAdminCustomerToggle(e);
    props.setClient(e);
    props.setPage("ClientProfile");
  };

  const filterCustomer = () => {
    let filtered = clients;

    // Apply adminCustomerToggle if set
    if (adminCustomerToggle) {
      props.setPage("ClientProfile");
      filtered = filtered.filter(
        (customer) => customer.id === adminCustomerToggle,
      );
    }

    // Apply search filter
    if (debouncedSearchQuery) {
      filtered = filtered.filter(
        (customer) =>
          customer.name
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          customer.id
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()),
      );
    }

    setDisplayClients(filtered);
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    getClientList();
  }, []);

  useEffect(() => {
    filterCustomer();
  }, [clients, adminCustomerToggle, debouncedSearchQuery]);

  return (
    <CustomersSC>
      {loadingClients ? (
        <Loader
          centered
          variant="glass"
          size="md"
          content="Loading customers..."
        />
      ) : window.screen.width >= 768 ? (
        <ListTable>
          <ListHeader>
            <HeaderCard>
              <FlexboxGrid>
                <FlexboxGrid.Item colspan={8}>
                  <HeaderTitle>Customer Directory</HeaderTitle>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={4} align="center">
                  <HeaderStat>
                    <HeaderStatNumber>{displayClients.length}</HeaderStatNumber>
                    <HeaderStatLabel>Customers</HeaderStatLabel>
                  </HeaderStat>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={4} align="center">
                  <HeaderStat>
                    <HeaderStatNumber>
                      {displayClients.reduce(
                        (sum, c) => sum + (c.clients?.length || 0),
                        0,
                      )}
                    </HeaderStatNumber>
                    <HeaderStatLabel>Total Users</HeaderStatLabel>
                  </HeaderStat>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={4} align="center">
                  <HeaderStat>
                    <HeaderStatNumber>
                      {displayClients.reduce(
                        (sum, c) => sum + (parseInt(c.site_count) || 0),
                        0,
                      )}
                    </HeaderStatNumber>
                    <HeaderStatLabel>Total Sites</HeaderStatLabel>
                  </HeaderStat>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={4} align="center">
                  <HeaderStat>
                    <HeaderStatNumber>
                      {displayClients.reduce(
                        (sum, c) => sum + (parseInt(c.systems_count) || 0),
                        0,
                      )}
                    </HeaderStatNumber>
                    <HeaderStatLabel>Total Systems</HeaderStatLabel>
                  </HeaderStat>
                </FlexboxGrid.Item>
              </FlexboxGrid>
              <SearchFieldContainer>
                <SearchField
                  placeholder="Search customers by name or ID..."
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
              </SearchFieldContainer>
            </HeaderCard>
          </ListHeader>
          <CustomerCardsContainer>
            {displayClients?.map((client, index) => (
              <GlassCard
                key={client.id}
                animated
                visible={cardsVisible}
                delay={index * 50}
                hoverable
                onClick={() => handleSelect(client.id)}
              >
                <CustomerCardContent>
                  <LogoSection>
                    {client.logo_src ? (
                      <CustomerLogo>
                        {sanitizeCustomerLogo(clientLogos[index])}
                      </CustomerLogo>
                    ) : (
                      <DefaultLogoIcon>
                        <ImageIcon />
                      </DefaultLogoIcon>
                    )}
                  </LogoSection>

                  <CustomerInfo>
                    <CustomerName>{client.name}</CustomerName>
                    <CustomerID>{client.id}</CustomerID>
                  </CustomerInfo>

                  <StatsBadgeContainer>
                    <StatItem>
                      <StatLabel>Users</StatLabel>
                      <Badge
                        variant={
                          client.clients?.length > 0 ? "info" : "neutral"
                        }
                      >
                        {client.clients?.length > 0
                          ? client.clients.length
                          : "0"}
                      </Badge>
                    </StatItem>

                    <StatItem>
                      <StatLabel>Sites</StatLabel>
                      <Badge
                        variant={client.site_count > 0 ? "info" : "neutral"}
                      >
                        {client.site_count || "0"}
                      </Badge>
                    </StatItem>

                    <StatItem>
                      <StatLabel>Groups</StatLabel>
                      <Badge
                        variant={
                          client.site_group_count > 0 ? "success" : "neutral"
                        }
                      >
                        {client.site_group_count > 0
                          ? client.site_group_count
                          : "0"}
                      </Badge>
                    </StatItem>

                    <StatItem>
                      <StatLabel>Systems</StatLabel>
                      <Badge
                        variant={
                          client.systems_count > 0 ? "primary" : "neutral"
                        }
                      >
                        {client.systems_count || "0"}
                      </Badge>
                    </StatItem>
                  </StatsBadgeContainer>
                </CustomerCardContent>
              </GlassCard>
            ))}
          </CustomerCardsContainer>
        </ListTable>
      ) : (
        <MobileListTable>
          <MobileCustomerCardsContainer>
            {displayClients?.map((client, index) => (
              <GlassCard
                key={client.id}
                animated
                visible={cardsVisible}
                delay={index * 50}
                hoverable
                onClick={() => handleSelect(client.id)}
              >
                <MobileCustomerCardContent>
                  <MobileLogoSection>
                    {client.logo_src ? (
                      <CustomerLogo>
                        {sanitizeCustomerLogo(clientLogos[index])}
                      </CustomerLogo>
                    ) : (
                      <DefaultLogoIcon>
                        <ImageIcon />
                      </DefaultLogoIcon>
                    )}
                  </MobileLogoSection>

                  <MobileCustomerInfo>
                    <CustomerName>{client.name}</CustomerName>
                    <CustomerID>{client.id}</CustomerID>
                  </MobileCustomerInfo>

                  <MobileStatsContainer>
                    <Badge
                      variant={client.clients?.length > 0 ? "info" : "neutral"}
                    >
                      {client.clients?.length > 0
                        ? `${client.clients.length} Users`
                        : "No Users"}
                    </Badge>
                  </MobileStatsContainer>
                </MobileCustomerCardContent>
              </GlassCard>
            ))}
          </MobileCustomerCardsContainer>
        </MobileListTable>
      )}
    </CustomersSC>
  );
};

const CustomersSC = styled.div`
  display: block;
  width: 100%;
  height: auto;
  text-align: -webkit-center;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};
`;

const ListTable = styled.div`
  width: 100%;
  height: auto;
  padding-top: 5vh;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};
  text-align: start;

  @media screen and (min-width: 769px) {
    width: 80%;
  }
`;

const MobileListTable = styled.div`
  width: 100%;
  height: auto;
  padding-top: 2vh;
  text-align: start;
  padding: 16px 8px;
`;

const ListHeader = styled.div`
  margin-bottom: 20px;
`;

const HeaderCard = styled.div`
  background: linear-gradient(135deg, #2c5aa0 0%, #4a7bc8 100%);
  border-radius: 25px;
  padding: 16px 20px;
  color: white;
  box-shadow: 0 4px 12px rgba(44, 90, 160, 0.3);
`;

const HeaderTitle = styled.div`
  font-size: 1.4em;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  height: 100%;
`;

const SearchFieldContainer = styled.div`
  max-width: 500px;
  margin-top: 16px;

  .rs-input-group {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 50px;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
      border-color: rgba(255, 255, 255, 0.4);
    }

    input {
      background: transparent !important;
      color: white !important;
      border: none !important;

      &::placeholder {
        color: rgba(255, 255, 255, 0.7) !important;
      }

      &:focus {
        background: transparent !important;
      }
    }

    .rs-input-group-addon {
      background: transparent;
      border: none;

      svg {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
`;

const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const HeaderStatNumber = styled.div`
  font-size: 1.8em;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const HeaderStatLabel = styled.div`
  font-size: 0.8em;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CustomerCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-bottom: 40px;
`;

const MobileCustomerCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-bottom: 32px;
`;

const CustomerCardContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 4px 0;
`;

const MobileCustomerCardContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 4px 0;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 60px;
`;

const MobileLogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  height: 50px;
`;

const CustomerLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const DefaultLogoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #95a5a6;
  font-size: 2.5em;
`;

const CustomerInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const MobileCustomerInfo = styled.div`
  flex: 1;
  min-width: 150px;
`;

const CustomerName = styled.div`
  font-size: 1.1em;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
`;

const CustomerID = styled.div`
  font-size: 0.8em;
  color: #7f8c8d;
  font-weight: normal;
`;

const StatsBadgeContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media screen and (max-width: 1200px) {
    gap: 8px;
  }
`;

const MobileStatsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 60px;
`;

const StatLabel = styled.div`
  font-size: 0.7em;
  font-weight: 600;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ImgSrcLogo = styled.img`
  width: auto;
  height: 40px;
  max-width: 100%;
  object-fit: contain;
`;
